import {
  Box,
  Checkbox,
  FormControl,
  MenuItem,
  Select,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import TextFieldComponent from "components/input/TextFieldComponent";
import TableCellComponent from "components/table/TableCellComponent";
import TableRowComponent from "components/table/TableRowComponent";
import { HEADER_EST_RESULT, HEADER_EST_SUB_RESULT } from "constants/data";
import { formatCurrency } from "utils";
import { Fragment, useState } from "react";
import generateUniqueId from "util/generate_id";
import { REMARK_MAX_LENGTH } from "constants/format";
import { EstQuoteDetailTire } from "../types";
import { getRoundedValue } from "../api/carModelApi";

const StyledTableCell = styled(TableCell)({
  border: "1px solid white",
  textAlign: "center",
  fontWeight: "bold",
  fontSize: "20px",
  backgroundColor: "var(--primary-500)",
  width: "calc((100% - 8px) / 5)",
});

/**
 * TableEstResult component renders a detailed estimation result table for car tires.
 * @param {object} props - The component props.
 * @param {number} props.rounding - The rounding option for price calculations.
 * @param {EstQuoteDetailTire[]} props.quoteDetailTire - Array of tire quote details to display.
 * @param {(key: keyof EstQuoteDetailTire, index: number, value: any) => void} props.setEstResultDetail - Callback to update a specific field in a tire detail.
 */
export default function TableEstResult({
  rounding,
  quoteDetailTire,
  setEstResultDetail,
  cashbackPeriod,
}: {
  rounding: number;
  quoteDetailTire: EstQuoteDetailTire[];
  setEstResultDetail: (key: keyof EstQuoteDetailTire, index: number, value: any) => void;
  cashbackPeriod?: number;
}) {
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

  return (
    <TableContainer component={Table} sx={{ boxShadow: "none", minWidth: "1300px", width: "100%" }}>
      <TableHead>
        <TableRow>
          {HEADER_EST_RESULT.map((item, index) =>
            item.label !== "本数" ? (
              <TableCellComponent {...item} key={generateUniqueId(index)} type="header" />
            ) : (
              <StyledTableCell
                key={generateUniqueId(index)}
                sx={{ whiteSpace: "nowrap", width: 100 }}
              >
                <Box sx={{ display: "flex", flexDirection: "column", rowGap: "18px" }}>
                  <Box>本数</Box>
                  <FormControl fullWidth>
                    <Select
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      value={String(quoteDetailTire[0]?.quantity || 4)}
                      label=""
                      sx={{
                        height: "42px",
                        backgroundColor: "var(--white)",
                      }}
                      disabled
                    >
                      <MenuItem value="1">1</MenuItem>
                      <MenuItem value="2">2</MenuItem>
                      <MenuItem value="4">4</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </StyledTableCell>
            ),
          )}
        </TableRow>
        <TableRowComponent data={HEADER_EST_SUB_RESULT} type="header" />
      </TableHead>
      <TableBody>
        {quoteDetailTire.map((item, index) => {
          const handleRowClick = (e: React.MouseEvent<HTMLTableRowElement>) => {
            // Don't toggle if clicking on checkbox or remarks field
            const target = e.target as HTMLElement;
            const isCheckbox = target.closest('input[type="checkbox"]');
            const isRemarksField = target.closest('[data-remarks-cell]');

            if (!isCheckbox && !isRemarksField) {
              setEstResultDetail("checked", index, !item.checked);
            }
          };

          const isHovered = hoveredRowIndex === index;

          return (
            // eslint-disable-next-line react/no-array-index-key
            <Fragment key={`${item.tire_item_cd}_${index}`}>
              <TableRow
                onClick={handleRowClick}
                onMouseEnter={() => setHoveredRowIndex(index)}
                onMouseLeave={() => setHoveredRowIndex(null)}
                sx={{
                  backgroundColor: isHovered
                    ? "var(--silver-100) !important"
                    : index % 2 !== 0
                      ? "var(--offwhite-200)"
                      : "var(--white)",
                  height: "60px",
                  cursor: "pointer",
                }}
              >
                <TableCellComponent rowSpan={2} sx={{ border: "1px solid var(--offwhite-500)" }}>
                  <Checkbox
                    color="secondary"
                    inputProps={{
                      "aria-label": "select all desserts",
                    }}
                    checked={item.checked}
                    onChange={(e) => {
                      setEstResultDetail("checked", index, e.target.checked);
                    }}
                  />
                </TableCellComponent>
                <TableCellComponent
                  sx={{ textAlign: "center", border: "1px solid var(--offwhite-500)" }}
                >
                  {item.maker_name}
                </TableCellComponent>
                <TableCellComponent
                  sx={{ textAlign: "center", border: "1px solid var(--offwhite-500)" }}
                >
                  {item.tire_loin_speed_mark}
                </TableCellComponent>
                <TableCellComponent
                  sx={{ textAlign: "center", border: "1px solid var(--offwhite-500)" }}
                >
                  {item.quantity}
                </TableCellComponent>
                <TableCellComponent
                  sx={{ textAlign: "center", border: "1px solid var(--offwhite-500)" }}
                >
                  {item.special_discount ?? item.fixed_discount ?? 0}
                </TableCellComponent>
                <TableCellComponent
                  sx={{ textAlign: "right", border: "1px solid var(--offwhite-500)" }}
                >
                  {formatCurrency(item.wage_cost_price ?? 0)}
                </TableCellComponent>
                <TableCellComponent
                  sx={{ textAlign: "right", border: "1px solid var(--offwhite-500)" }}
                >
                  {formatCurrency(item.puncture_protection_price ?? 0)}
                </TableCellComponent>
                <TableCellComponent
                  sx={{ textAlign: "right", border: "1px solid var(--offwhite-500)" }}
                >
                  {cashbackPeriod === 2 ? formatCurrency(item.cb_tire_price ?? 0) : "0"}
                </TableCellComponent>
                <TableCellComponent
                  read-only
                  sx={{
                    textAlign: "left",
                    border: "1px solid var(--offwhite-500)",
                    py: 1,
                    minWidth: "256px",
                  }}
                >
                  {item.remarks}
                </TableCellComponent>
              </TableRow>
              <TableRow
                onClick={handleRowClick}
                onMouseEnter={() => setHoveredRowIndex(index)}
                onMouseLeave={() => setHoveredRowIndex(null)}
                sx={{
                  backgroundColor: isHovered
                    ? "var(--silver-100) !important"
                    : index % 2 !== 0
                      ? "var(--offwhite-200)"
                      : "var(--white)",
                  height: "60px",
                  cursor: "pointer",
                }}
              >
                <TableCellComponent
                  sx={{ textAlign: "center", border: "1px solid var(--offwhite-500)" }}
                >
                  {item.item_name}
                </TableCellComponent>
                <TableCellComponent
                  sx={{ textAlign: "center", border: "1px solid var(--offwhite-500)" }}
                >
                  {item.tire_label}
                </TableCellComponent>
                <TableCellComponent
                  sx={{ textAlign: "center", border: "1px solid var(--offwhite-500)" }}
                >
                  {formatCurrency(item.nomal_price)}
                </TableCellComponent>
                <TableCellComponent
                  sx={{ textAlign: "center", border: "1px solid var(--offwhite-500)" }}
                >
                  {item.discounted_sale_price && formatCurrency(item.discounted_sale_price)}
                </TableCellComponent>
                <TableCellComponent
                  sx={{ textAlign: "right", border: "1px solid var(--offwhite-500)" }}
                >
                  {formatCurrency(item.wage_cost_in_price)}
                </TableCellComponent>
                <TableCellComponent
                  sx={{ textAlign: "right", border: "1px solid var(--offwhite-500)" }}
                >
                  {item.puncture_protection_in_price &&
                    formatCurrency(item.puncture_protection_in_price)}
                </TableCellComponent>
                <TableCellComponent
                  sx={{ textAlign: "right", color: "var(--red-800)", fontWeight: "bold" }}
                >
                  {formatCurrency(getRoundedValue(item.confirm_total_price ?? 0, rounding))}
                </TableCellComponent>
                <TableCellComponent
                  data-remarks-cell
                  sx={{
                    textAlign: "left",
                    border: "1px solid var(--offwhite-500)",
                    py: 1,
                    minWidth: "256px",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <TextFieldComponent
                    multiline
                    sx={{ height: "100%", py: 0, my: 0 }}
                    sxRoot={{
                      height: "100%",
                      alignItems: "start",
                      fontSize: { xs: "16px", md: "18px", xl: "20px" },
                    }}
                    sxInput={{ p: 0, height: "100%" }}
                    value={item.input_remarks}
                    onChangeText={(value) => setEstResultDetail("input_remarks", index, value)}
                    validateOption={{
                      maxLength: { message: "", value: REMARK_MAX_LENGTH },
                    }}
                  />
                </TableCellComponent>
              </TableRow>
            </Fragment>
          );
        })}
      </TableBody>
    </TableContainer>
  );
}
